import { useState, useEffect, useRef } from 'react';
import API from './api';

function getUserRole() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try { return JSON.parse(atob(token.split('.')[1])).role; } catch { return null; }
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function fileIcon(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return '📄';
  if (ext === 'xlsx' || ext === 'xls') return '📊';
  if (ext === 'docx' || ext === 'doc') return '📝';
  if (ext === 'pptx' || ext === 'ppt') return '📑';
  return '📎';
}

// Build a recursive tree from flat file list using subdir paths
function buildTree(files) {
  const root = { files: [], folders: {} };
  const sorted = [...files].sort((a, b) => a.filename.localeCompare(b.filename));
  for (const f of sorted) {
    const parts = f.subdir ? f.subdir.split('/') : [];
    let node = root;
    for (const part of parts) {
      if (!node.folders[part]) node.folders[part] = { files: [], folders: {} };
      node = node.folders[part];
    }
    node.files.push(f);
  }
  return root;
}

function countTreeFiles(node) {
  let count = node.files.length;
  for (const sub of Object.values(node.folders)) count += countTreeFiles(sub);
  return count;
}

const CATEGORY_ORDER = ['general', 'shore', 'circular_letters'];

// Recursive folder node component
function FolderNode({ name, fullPath, node, depth, docId, category, isAdmin, deletingFile, onDeleteFile, onDeleteFolder }) {
  const [open, setOpen] = useState(true);
  const folderNames = Object.keys(node.folders).sort((a, b) => a.localeCompare(b));
  const fileCount = countTreeFiles(node);
  const delKey = `folder::${docId}::${fullPath}`;

  return (
    <div style={{ marginLeft: depth > 0 ? 20 : 0 }}>
      {name && (
        <div style={{ ...s.subfolderHeader, paddingLeft: '12px' }}>
          <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => setOpen(!open)}>
            {open ? '▼' : '▶'} 📁
          </span>
          <span style={s.subfolderName} onClick={() => setOpen(!open)}>{name}</span>
          <span style={s.subfolderCount}>{fileCount} files</span>
          {isAdmin && (
            <button
              style={s.folderDeleteBtn}
              disabled={deletingFile === delKey}
              onClick={() => onDeleteFolder(docId, fullPath, fileCount)}
            >
              {deletingFile === delKey ? '…' : 'DELETE'}
            </button>
          )}
        </div>
      )}
      {open && (
        <div style={name ? { borderLeft: '1px solid rgba(201,168,76,0.15)', marginLeft: 14, paddingLeft: 0 } : {}}>
          {folderNames.map(sub => (
            <FolderNode
              key={sub}
              name={sub}
              fullPath={fullPath ? `${fullPath}/${sub}` : sub}
              node={node.folders[sub]}
              depth={depth + 1}
              docId={docId}
              category={category}
              isAdmin={isAdmin}
              deletingFile={deletingFile}
              onDeleteFile={onDeleteFile}
              onDeleteFolder={onDeleteFolder}
            />
          ))}
          {node.files.map((f, idx) => (
            <div key={idx} style={{ ...s.fileRow, paddingLeft: name ? '28px' : '12px' }}>
              <span style={s.fileIcon}>{fileIcon(f.filename)}</span>
              <div style={s.fileInfo}>
                <span style={s.fileName}>{f.filename}</span>
                <span style={s.fileMeta}>
                  {formatSize(f.file_size)} · {formatDate(f.uploaded_at)} · {f.uploaded_by}
                </span>
              </div>
              {isAdmin && (
                <button
                  style={s.deleteBtn}
                  disabled={deletingFile === `file::${docId}::${f.subdir}::${f.filename}`}
                  onClick={() => onDeleteFile(docId, f.filename, f.subdir || '')}
                >
                  {deletingFile === `file::${docId}::${f.subdir}::${f.filename}` ? '…' : 'DELETE'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function IMSManager({ setPage, onLogout }) {
  const [structure, setStructure] = useState(null);
  const [activeCategory, setActiveCategory] = useState('general');
  const [expandedSection, setExpandedSection] = useState(null);
  const [uploadingSection, setUploadingSection] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [deletingFile, setDeletingFile] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [reindexProgress, setReindexProgress] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const pendingSectionRef = useRef(null);
  const reindexPollRef = useRef(null);

  const isAdmin = getUserRole() === 'admin';

  useEffect(() => { fetchStructure(); }, []);

  const fetchStructure = async () => {
    setLoading(true);
    try {
      const res = await API.get('/ims/structure');
      setStructure(res.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
      showStatus('Failed to load structure.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showStatus = (msg, type = 'ok') => {
    setStatusMsg({ msg, type });
    setTimeout(() => setStatusMsg(null), 5000);
  };

  const uploadFiles = async (docId, filesWithPath) => {
    setUploadingSection(docId);
    const total = filesWithPath.length;
    let successCount = 0;

    for (let i = 0; i < filesWithPath.length; i++) {
      const { file, relativePath } = filesWithPath[i];
      const form = new FormData();
      form.append('file', file);
      form.append('relative_path', relativePath || file.name);
      setUploadProgress({ current: i + 1, total, filename: file.name, filePct: 0 });
      try {
        await API.post(`/ims/upload/${activeCategory}/${docId}`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const filePct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setUploadProgress({ current: i + 1, total, filename: file.name, filePct });
          },
        });
        successCount++;
      } catch (e) {
        showStatus(`Hata: ${e.response?.data?.detail || file.name}`, 'error');
      }
    }

    setUploadProgress(null);
    if (successCount > 0) {
      showStatus(`${successCount} / ${total} files uploaded.`, 'ok');
      await fetchStructure();
    }
    setUploadingSection(null);
    pendingSectionRef.current = null;
  };

  const handleFileClick = (docId) => {
    pendingSectionRef.current = docId;
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !pendingSectionRef.current) return;
    await uploadFiles(pendingSectionRef.current, files.map(f => ({ file: f, relativePath: f.name })));
  };

  const handleFolderClick = (docId) => {
    pendingSectionRef.current = docId;
    folderInputRef.current.value = '';
    folderInputRef.current.click();
  };

  const handleFolderChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !pendingSectionRef.current) return;
    await uploadFiles(pendingSectionRef.current, files.map(f => ({
      file: f,
      relativePath: f.webkitRelativePath || f.name,
    })));
  };

  const handleDeleteFile = async (docId, filename, subdir) => {
    if (!window.confirm(`"${filename}" will be deleted. Are you sure?`)) return;
    const key = `file::${docId}::${subdir}::${filename}`;
    setDeletingFile(key);
    try {
      await API.delete(`/ims/files/${activeCategory}/${docId}`, { params: { filename, subdir: subdir || '' } });
      showStatus('File deleted.', 'ok');
      await fetchStructure();
    } catch (e) {
      showStatus(e.response?.data?.detail || 'Delete failed.', 'error');
    } finally { setDeletingFile(null); }
  };

  const handleDeleteFolder = async (docId, subdirPrefix, fileCount) => {
    const name = subdirPrefix.split('/').pop();
    if (!window.confirm(`Folder "${name}" and its ${fileCount} files will be deleted. Are you sure?`)) return;
    const key = `folder::${docId}::${subdirPrefix}`;
    setDeletingFile(key);
    try {
      await API.delete(`/ims/folders/${activeCategory}/${docId}`, { params: { subdir_prefix: subdirPrefix } });
      showStatus(`"${name}" folder deleted.`, 'ok');
      await fetchStructure();
    } catch (e) {
      showStatus(e.response?.data?.detail || 'Delete failed.', 'error');
    } finally { setDeletingFile(null); }
  };

  const handleDeleteSection = async (docId, fileCount) => {
    if (!window.confirm(`All ${fileCount} files in this section will be deleted. Are you sure?`)) return;
    const key = `section::${docId}`;
    setDeletingFile(key);
    try {
      await API.delete(`/ims/sections/${activeCategory}/${docId}`);
      showStatus('Section cleared.', 'ok');
      setExpandedSection(null);
      await fetchStructure();
    } catch (e) {
      showStatus(e.response?.data?.detail || 'Delete failed.', 'error');
    } finally { setDeletingFile(null); }
  };

  const handleReindex = async () => {
    if (!window.confirm('Reindex all QDMS files?')) return;
    setReindexing(true);
    setReindexProgress({ current: 0, total: 0, current_file: '', indexed: 0 });

    // Start polling progress every 500ms
    reindexPollRef.current = setInterval(async () => {
      try {
        const p = await API.get('/ims/reindex/progress');
        setReindexProgress(p.data);
        if (!p.data.running && p.data.total > 0) {
          clearInterval(reindexPollRef.current);
        }
      } catch { /* ignore poll errors */ }
    }, 500);

    try {
      const res = await API.post('/ims/reindex');
      showStatus(`${res.data.indexed} files reindexed.`, 'ok');
    } catch { showStatus('Reindex failed.', 'error'); }
    finally {
      clearInterval(reindexPollRef.current);
      setReindexing(false);
      setReindexProgress(null);
    }
  };

  const summary = structure?.summary || { total_sections: 0, sections_with_files: 0, total_files: 0 };
  const categories = structure?.categories || {};
  const currentDocs = [...(categories[activeCategory]?.documents || [])].sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));

  return (
    <div style={s.wrapper}>
      <header style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.logoIcon}>MP</div>
          <div>
            <div style={s.logoTitle}>QDMS</div>
            <div style={s.logoSub}>QUALITY DOCUMENT MANAGEMENT SYSTEM</div>
          </div>
        </div>
        <div style={s.headerRight}>
          {isAdmin && (
            <button style={s.reindexBtn} onClick={handleReindex} disabled={reindexing}>
              {reindexing ? 'REINDEXING…' : 'REINDEX'}
            </button>
          )}
          <button style={s.guideBtn} onClick={() => setShowGuide(true)}>? How to Guide</button>
          <button style={s.backBtn} onClick={() => setPage('dashboard')}>← BACK</button>
          <button style={s.logoutBtn} onClick={onLogout}>LOGOUT</button>
        </div>
      </header>

      {showGuide && (
        <div style={s.overlay} onClick={() => setShowGuide(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div>
                <div style={s.modalTitle}>QDMS — How to Guide</div>
                <div style={s.modalSub}>Quality Document Management System</div>
              </div>
              <button style={s.modalClose} onClick={() => setShowGuide(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.gSection}>
                <div style={s.gTitle}>What is QDMS?</div>
                <div style={s.gText}>The Quality Document Management System provides <strong style={s.hl}>structured, numbered document slots</strong> for your fleet's management system documents. Every slot has a fixed ID and title — you upload the document into the right slot. All uploaded documents are automatically indexed for AI search across the platform.</div>
              </div>
              <div style={s.gSection}>
                <div style={s.gTitle}>Document Categories</div>
                {[
                  { cat: 'GENERAL', count: '14 slots', desc: 'Fleet manuals, safety manuals, engine/bridge manuals, emergency manual, job descriptions. Slot #14 is the Job Description — required by BlameCatcher.' },
                  { cat: 'SHORE', count: '12 slots', desc: 'Shore-side department manuals: SSE, Fleet, Crew, Digital, Dry Dock, Procurement, Warehouse, New Building, Finance, HR, Project, Technical.' },
                  { cat: 'CIRCULAR LETTERS', count: '7 categories', desc: 'Company directives organized by department: SSE (100-series), Fleet (200), Technical (300), Dry Dock (400), Procurement (500), HR (600), Cargo (700).' },
                ].map(c => (
                  <div key={c.cat} style={s.catRow}>
                    <span style={s.catBadge}>{c.cat}</span>
                    <span style={s.catCount}>{c.count}</span>
                    <span style={s.gText}>{c.desc}</span>
                  </div>
                ))}
              </div>
              <div style={s.gSection}>
                <div style={s.gTitle}>Step-by-Step</div>
                {[
                  { n:'1', t:'Select a category tab', d:'Click GENERAL, SHORE, or CIRCULAR LETTERS at the top of the page to switch category.' },
                  { n:'2', t:'Find the target slot', d:'Each slot shows its number, title, and upload status (green = uploaded, grey = empty). Click a slot to expand it.' },
                  { n:'3', t:'Upload your document', d:'Click Upload in an expanded slot. Accepted formats: PDF, DOCX, XLSX, PPTX, TXT. The file replaces any previous version in that slot.' },
                  { n:'4', t:'Document is indexed automatically', d:'After upload, the system indexes the file content into ChromaDB. It becomes immediately searchable by Audit Assistant and QDMS Analyzer.' },
                ].map(step => (
                  <div key={step.n} style={s.step}>
                    <div style={s.stepN}>{step.n}</div>
                    <div><div style={s.stepT}>{step.t}</div><div style={s.gText}>{step.d}</div></div>
                  </div>
                ))}
              </div>
              <div style={s.gSection}>
                <div style={s.gTitle}>Important Notes</div>
                <div style={s.noteList}>
                  {['Only admins can upload, replace, or delete documents. All users can view.',
                    'GENERAL slot #14 (Job Description) is required for BlameCatcher to work.',
                    'Click REINDEX if AI search seems to return outdated results.',
                    'Circular Letters can have multiple files per slot — upload additional documents without overwriting.',
                  ].map((n,i) => <div key={i} style={s.noteItem}><span style={s.noteDot}>—</span><span>{n}</span></div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {statusMsg && (
        <div style={{
          ...s.statusBar,
          background: statusMsg.type === 'error' ? 'rgba(232,0,29,0.12)' : 'rgba(0,196,140,0.12)',
          borderColor: statusMsg.type === 'error' ? 'rgba(232,0,29,0.3)' : 'rgba(0,196,140,0.3)',
          color: statusMsg.type === 'error' ? '#ff6b7a' : '#00c48c',
        }}>
          {statusMsg.msg}
        </div>
      )}

      {uploadProgress && (
        <div style={s.progressWrapper}>
          <div style={s.progressTop}>
            <span style={s.progressLabel}>UPLOADING — {uploadProgress.current} / {uploadProgress.total} files</span>
            <span style={s.progressLabel}>
              {Math.round(((uploadProgress.current - 1 + uploadProgress.filePct / 100) / uploadProgress.total) * 100)}%
            </span>
          </div>
          <div style={s.progressFilename}>{uploadProgress.filename}</div>
          <div style={s.progressTrack}>
            <div style={{
              ...s.progressBar,
              width: `${Math.round(((uploadProgress.current - 1 + uploadProgress.filePct / 100) / uploadProgress.total) * 100)}%`,
            }} />
          </div>
        </div>
      )}

      {reindexProgress && (
        <div style={s.reindexProgressWrapper}>
          <div style={s.progressTop}>
            <span style={s.reindexProgressLabel}>
              INDEXING — {reindexProgress.current} / {reindexProgress.total || '…'} files
            </span>
            <span style={s.reindexProgressLabel}>
              {reindexProgress.total > 0 ? `${Math.round((reindexProgress.current / reindexProgress.total) * 100)}%` : '0%'}
            </span>
          </div>
          <div style={s.progressFilename}>
            {reindexProgress.current_file || 'Preparing…'}
          </div>
          <div style={s.progressTrack}>
            <div style={{
              ...s.reindexProgressBar,
              width: reindexProgress.total > 0
                ? `${Math.round((reindexProgress.current / reindexProgress.total) * 100)}%`
                : '0%',
            }} />
          </div>
        </div>
      )}

      <div style={s.statsBar}>
        <div style={s.statItem}>
          <span style={s.statNum}>{summary.sections_with_files}</span>
          <span style={s.statLabel}>/ {summary.total_sections} SECTIONS FILLED</span>
        </div>
        <div style={s.statDivider} />
        <div style={s.statItem}>
          <span style={s.statNum}>{summary.total_files}</span>
          <span style={s.statLabel}>TOTAL FILES</span>
        </div>
        {!isAdmin && <><div style={s.statDivider} /><div style={{ fontSize: '11px', color: 'var(--grey)' }}>View only</div></>}
      </div>

      <div style={s.body}>
        <aside style={s.sidebar}>
          <div style={s.sidebarTitle}>CATEGORIES</div>
          {CATEGORY_ORDER.map(key => {
            const cat = categories[key];
            if (!cat) return null;
            const filled = cat.documents.filter(d => d.file_count > 0).length;
            const total = cat.documents.length;
            return (
              <div key={key}
                style={activeCategory === key ? s.sidebarItemActive : s.sidebarItem}
                onClick={() => { setActiveCategory(key); setExpandedSection(null); }}
              >
                <span>{cat.label}</span>
                <span style={{ ...s.sidebarCount, color: filled === total ? '#00c48c' : filled > 0 ? '#c9a84c' : 'var(--grey)' }}>
                  {filled}/{total}
                </span>
              </div>
            );
          })}
        </aside>

        <main style={s.main}>
          {loading ? (
            <div style={s.loadingText}>Loading...</div>
          ) : (
            <>
              <div style={s.catHeader}>
                <span style={s.catTitle}>{categories[activeCategory]?.label}</span>
                <span style={s.catSub}>
                  {currentDocs.filter(d => d.file_count > 0).length} / {currentDocs.length} sections filled
                  {' · '}
                  {currentDocs.reduce((acc, d) => acc + d.file_count, 0)} files
                </span>
              </div>

              <div style={s.docList}>
                {currentDocs.map(doc => {
                  const isExpanded = expandedSection === doc.id;
                  const uploading = uploadingSection === doc.id;
                  const hasFiles = doc.file_count > 0;
                  const tree = buildTree(doc.files || []);

                  return (
                    <div key={doc.id} style={hasFiles ? s.docCardFilled : s.docCard}>
                      <div style={s.docHeaderRow} onClick={() => hasFiles && setExpandedSection(isExpanded ? null : doc.id)}>
                        <div style={hasFiles ? s.docNumFilled : s.docNum}>{doc.id}</div>
                        <div style={s.docInfo}>
                          <div style={s.docTitle}>{doc.title}</div>
                          <div style={s.docMeta}>
                            {hasFiles
                              ? <span style={{ color: '#00c48c' }}>{doc.file_count} files uploaded</span>
                              : <span style={s.docEmpty}>No files uploaded yet</span>
                            }
                          </div>
                        </div>
                        <div style={s.docActions} onClick={e => e.stopPropagation()}>
                          {hasFiles && <span style={s.expandBadge}>{isExpanded ? '▲' : '▼'} {doc.file_count}</span>}
                          {isAdmin && (
                            <>
                              <button style={s.uploadBtn} disabled={uploading} onClick={() => handleFileClick(doc.id)}>
                                {uploading ? '…' : '+ FILE'}
                              </button>
                              <button style={s.folderBtn} disabled={uploading} onClick={() => handleFolderClick(doc.id)}>
                                📁 FOLDER
                              </button>
                              {hasFiles && (
                                <button
                                  style={s.deleteBtn}
                                  disabled={deletingFile === `section::${doc.id}`}
                                  onClick={() => handleDeleteSection(doc.id, doc.file_count)}
                                >
                                  {deletingFile === `section::${doc.id}` ? '…' : 'DELETE ALL'}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {isExpanded && hasFiles && (
                        <div style={s.fileList}>
                          <FolderNode
                            name=""
                            fullPath=""
                            node={tree}
                            depth={0}
                            docId={doc.id}
                            category={activeCategory}
                            isAdmin={isAdmin}
                            deletingFile={deletingFile}
                            onDeleteFile={handleDeleteFile}
                            onDeleteFolder={handleDeleteFolder}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </main>
      </div>

      <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileChange} />
      <input ref={folderInputRef} type="file" webkitdirectory="true" multiple style={{ display: 'none' }} onChange={handleFolderChange} />
    </div>
  );
}

const s = {
  wrapper: { minHeight: '100vh', background: 'var(--navy)', display: 'flex', flexDirection: 'column' },
  header: {
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 40px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  logoIcon: {
    width: '36px', height: '36px', background: 'var(--red)', borderRadius: '8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: '500', color: 'white',
  },
  logoTitle: { fontSize: '15px', fontWeight: '700' },
  logoSub: { fontSize: '10px', color: 'var(--grey)', letterSpacing: '2px', fontFamily: 'var(--font-mono)' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  reindexBtn: {
    background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)',
    color: 'var(--gold)', padding: '6px 16px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  backBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 16px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  logoutBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 16px', borderRadius: '6px',
    fontSize: '11px', letterSpacing: '1px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  statusBar: { padding: '10px 40px', fontSize: '13px', fontWeight: '600', border: '0', borderBottom: '1px solid', flexShrink: 0 },
  progressWrapper: {
    background: 'rgba(201,168,76,0.06)', borderBottom: '1px solid rgba(201,168,76,0.2)',
    padding: '10px 40px', flexShrink: 0,
  },
  reindexProgressWrapper: {
    background: 'rgba(56,189,248,0.06)', borderBottom: '1px solid rgba(56,189,248,0.2)',
    padding: '10px 40px', flexShrink: 0,
  },
  reindexProgressLabel: { fontSize: '11px', fontWeight: '700', color: '#38bdf8', fontFamily: 'var(--font-mono)', letterSpacing: '1px' },
  reindexProgressBar: { height: '100%', background: '#38bdf8', borderRadius: '2px', transition: 'width 0.3s ease' },
  progressTop: { display: 'flex', justifyContent: 'space-between', marginBottom: '4px' },
  progressLabel: { fontSize: '11px', fontWeight: '700', color: 'var(--gold)', fontFamily: 'var(--font-mono)', letterSpacing: '1px' },
  progressFilename: { fontSize: '11px', color: 'var(--grey)', marginBottom: '6px', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  progressTrack: { height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' },
  progressBar: { height: '100%', background: 'var(--gold)', borderRadius: '2px', transition: 'width 0.2s ease' },
  statsBar: {
    display: 'flex', alignItems: 'center',
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 40px', height: '52px', flexShrink: 0, gap: '0',
  },
  statItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  statNum: { fontSize: '20px', fontWeight: '800', fontFamily: 'var(--font-mono)', color: 'var(--white)' },
  statLabel: { fontSize: '10px', color: 'var(--grey)', letterSpacing: '1.5px' },
  statDivider: { width: '1px', height: '24px', background: 'var(--navy-border)', margin: '0 28px' },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  sidebar: {
    width: '220px', flexShrink: 0, borderRight: '1px solid var(--navy-border)',
    background: 'var(--navy-mid)', padding: '24px 0', overflowY: 'auto',
  },
  sidebarTitle: {
    fontSize: '10px', fontWeight: '700', letterSpacing: '2px',
    color: 'var(--grey)', padding: '0 20px 12px', fontFamily: 'var(--font-mono)',
  },
  sidebarItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: 'var(--grey)',
  },
  sidebarItemActive: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: 'var(--white)',
    background: 'rgba(255,255,255,0.06)', borderLeft: '3px solid var(--red)',
  },
  sidebarCount: { fontFamily: 'var(--font-mono)', fontSize: '12px' },
  main: { flex: 1, overflowY: 'auto', padding: '32px 40px' },
  loadingText: { textAlign: 'center', color: 'var(--grey)', padding: '60px', fontFamily: 'var(--font-mono)' },
  catHeader: { display: 'flex', alignItems: 'baseline', gap: '16px', marginBottom: '20px' },
  catTitle: { fontSize: '20px', fontWeight: '800', color: 'var(--white)' },
  catSub: { fontSize: '12px', color: 'var(--grey)' },
  docList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  docCard: { background: 'var(--navy-mid)', border: '1px solid var(--navy-border)', borderRadius: '10px', overflow: 'hidden' },
  docCardFilled: { background: 'rgba(0,196,140,0.03)', border: '1px solid rgba(0,196,140,0.2)', borderRadius: '10px', overflow: 'hidden' },
  docHeaderRow: { display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 20px', cursor: 'pointer' },
  docNum: {
    width: '44px', height: '44px', flexShrink: 0,
    background: 'rgba(136,146,176,0.1)', border: '1px solid rgba(136,146,176,0.2)',
    borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: '700', color: 'var(--grey)',
  },
  docNumFilled: {
    width: '44px', height: '44px', flexShrink: 0,
    background: 'rgba(0,196,140,0.12)', border: '1px solid rgba(0,196,140,0.3)',
    borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: '700', color: '#00c48c',
  },
  docInfo: { flex: 1, minWidth: 0 },
  docTitle: { fontSize: '14px', fontWeight: '600', color: 'var(--white)', marginBottom: '3px' },
  docMeta: { fontSize: '12px' },
  docEmpty: { color: 'rgba(136,146,176,0.5)', fontStyle: 'italic' },
  docActions: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  expandBadge: {
    fontSize: '11px', fontWeight: '700', color: '#00c48c',
    background: 'rgba(0,196,140,0.1)', border: '1px solid rgba(0,196,140,0.25)',
    borderRadius: '20px', padding: '3px 10px',
  },
  uploadBtn: {
    background: 'rgba(232,0,29,0.1)', border: '1px solid rgba(232,0,29,0.3)',
    color: '#ff6b7a', padding: '6px 12px', borderRadius: '6px',
    fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  folderBtn: {
    background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)',
    color: 'var(--gold)', padding: '6px 12px', borderRadius: '6px',
    fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  deleteBtn: {
    background: 'rgba(232,0,29,0.08)', border: '1px solid rgba(232,0,29,0.2)',
    color: '#ff6b7a', padding: '4px 10px', borderRadius: '5px',
    fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: 'var(--font-mono)', flexShrink: 0,
  },
  fileList: { borderTop: '1px solid var(--navy-border)', padding: '8px 12px 12px 12px' },
  subfolderHeader: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'rgba(201,168,76,0.06)', padding: '7px 12px',
    borderRadius: '6px', margin: '4px 0', cursor: 'default',
  },
  subfolderName: { fontSize: '13px', fontWeight: '700', color: 'var(--gold)', flex: 1, cursor: 'pointer' },
  subfolderCount: { fontSize: '11px', color: 'var(--grey)', fontFamily: 'var(--font-mono)' },
  folderDeleteBtn: {
    background: 'rgba(232,0,29,0.08)', border: '1px solid rgba(232,0,29,0.2)',
    color: '#ff6b7a', padding: '2px 8px', borderRadius: '4px',
    fontSize: '10px', fontWeight: '700', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  fileRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    background: 'rgba(0,0,0,0.12)', borderRadius: '5px', padding: '6px 12px', margin: '2px 0',
  },
  fileIcon: { fontSize: '14px', flexShrink: 0 },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: {
    display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--white)',
    fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  fileMeta: { display: 'block', fontSize: '11px', color: 'var(--grey)', marginTop: '1px' },
  guideBtn: { background:'transparent', border:'1px solid var(--navy-light)', color:'var(--grey)', padding:'5px 12px', borderRadius:6, fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'var(--font-mono)', whiteSpace:'nowrap' },
  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 },
  modal: { background:'#111d2e', border:'1px solid #1e2d40', borderRadius:16, width:'100%', maxWidth:700, maxHeight:'82vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 80px rgba(0,0,0,0.6)' },
  modalHeader: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'22px 26px 18px', borderBottom:'1px solid #1e2d40', flexShrink:0 },
  modalTitle: { fontSize:15, fontWeight:700, color:'#e8edf4', marginBottom:4 },
  modalSub: { fontSize:11, fontFamily:'var(--font-mono)', color:'var(--gold)', letterSpacing:1 },
  modalClose: { background:'transparent', border:'none', color:'var(--grey)', fontSize:16, cursor:'pointer', padding:'0 0 0 16px', lineHeight:1 },
  modalBody: { overflowY:'auto', padding:'22px 26px', display:'flex', flexDirection:'column', gap:22 },
  gSection: {},
  gTitle: { fontFamily:'var(--font-mono)', fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--gold)', marginBottom:10, textTransform:'uppercase' },
  gText: { fontSize:12, color:'#8a9ab0', lineHeight:1.7 },
  hl: { color:'#e8edf4' },
  catRow: { display:'flex', gap:10, alignItems:'flex-start', marginBottom:8, flexWrap:'wrap' },
  catBadge: { flexShrink:0, fontFamily:'var(--font-mono)', fontSize:10, fontWeight:700, color:'var(--gold)', border:'1px solid rgba(201,168,76,0.35)', padding:'2px 8px', borderRadius:4 },
  catCount: { flexShrink:0, fontSize:11, color:'#4a5a70', fontFamily:'var(--font-mono)', whiteSpace:'nowrap', paddingTop:2 },
  step: { display:'flex', gap:12, alignItems:'flex-start', marginBottom:10 },
  stepN: { flexShrink:0, width:26, height:26, borderRadius:'50%', background:'rgba(201,168,76,0.12)', border:'1px solid rgba(201,168,76,0.3)', color:'var(--gold)', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-mono)' },
  stepT: { fontSize:13, fontWeight:600, color:'#e8edf4', marginBottom:3 },
  noteList: { display:'flex', flexDirection:'column', gap:7 },
  noteItem: { display:'flex', gap:8, fontSize:12, color:'#8a9ab0', lineHeight:1.6 },
  noteDot: { color:'var(--gold)', flexShrink:0 },
};
